"use strict";
/**
 * Venue status: the EclipseGG platform's warn-then-suspend (owner, 2026-10-19:
 * "we must have an admin panel that we will be able to revoke a venue's perms
 * so they no longer can use the system"; docs/superpowers/specs/2026-10-19-
 * eclipsegg-platform-design.md §2).
 *
 * Two stored states, `Venue.status` ACTIVE or SUSPENDED. The warning before a
 * suspension is DERIVED, never stored: the server computes it from
 * `Venue.paidUntil` and the venue's own calendar date (device clocks drift)
 * and sends it as `serviceNotice`. The API, the staff website and the admin
 * panel all read the names and the day arithmetic from here, so "ends in 3
 * days" cannot mean one date on the server and another on a screen.
 *
 * The API decides suspension by the EXPLICIT value only (`status ===
 * 'SUSPENDED'`, never `!== 'ACTIVE'`; `isDemo === true`; a missing `paidUntil`
 * is not billed), so the many hand-built venue rows in its tests, which lack
 * the new columns, keep reading as an ordinary trading venue. The helpers
 * below follow the same rule.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SERVICE_NOTICE_DAYS = exports.PASSWORD_CHANGE_REQUIRED_CODE = exports.VENUE_SUSPENDED_MESSAGE = exports.VENUE_SUSPENDED_CODE = exports.VENUE_STATUSES = void 0;
exports.calendarDateIn = calendarDateIn;
exports.daysUntil = daysUntil;
exports.paidUntilLapsed = paidUntilLapsed;
exports.serviceNoticeFor = serviceNoticeFor;
const business_day_1 = require("./business-day");
/**
 * The states of `Venue.status`, in the Prisma enum's order. Declared again
 * here, not imported, because the browser must never pull in the Prisma
 * client; apps/api/src/auth/venue-status-migration.test.ts holds the two
 * lists equal.
 */
exports.VENUE_STATUSES = ['ACTIVE', 'SUSPENDED'];
/**
 * The 403 a suspended venue gets: every staff route, a refresh, a sign-in
 * after the RIGHT password (a wrong one is still 401, so the code cannot be
 * used to probe), every new sitting, portal sign-in and the portal shop.
 */
exports.VENUE_SUSPENDED_CODE = 'venue_suspended';
/**
 * Its message, fixed and the same for every venue. Never the seller's
 * `suspendedReason`, which stays inside the platform.
 */
exports.VENUE_SUSPENDED_MESSAGE = "This venue's EclipseGG service is suspended. Please contact EclipseGG.";
/**
 * The 403 while `Employee.mustChangePassword` is set (an owner the platform
 * created, or whose password it reset): every staff route but `GET /auth/me`,
 * `POST /auth/password` and the public ones (sign-out among them) answers it
 * until they choose their own.
 */
exports.PASSWORD_CHANGE_REQUIRED_CODE = 'password_change_required';
/** The banner shows once the last paid day is this many days away or fewer (spec §2). */
exports.SERVICE_NOTICE_DAYS = 7;
const MS_PER_DAY = 86_400_000;
/**
 * The CALENDAR date of `instant` in `timeZone` — not the business date: a
 * subscription paid until the 20th ends at the venue's midnight, whatever
 * hours the venue trades, not at its Opening Time.
 *
 * A zone `Intl` rejects falls back to {@link DEFAULT_VENUE_TIME_ZONE}, and so
 * does a missing or blank one, never the HOST's zone: business-day.ts never
 * consults the host clock, and neither does this. One venue's mistyped zone
 * must not stop its staff signing in.
 *
 * The blank-zone guard is belt-and-braces; without it the answer is the same.
 * `zonedPartsOf` already reads a missing zone as the default, and `Intl`
 * rejects a blank one with a RangeError, which the catch below turns into the
 * default. The guard keeps this function from relying on either.
 *
 * @throws TypeError when `instant` is not a valid Date.
 */
function calendarDateIn(instant, timeZone) {
    const zone = typeof timeZone === 'string' && timeZone.trim() !== '' ? timeZone : business_day_1.DEFAULT_VENUE_TIME_ZONE;
    let parts;
    try {
        parts = (0, business_day_1.zonedPartsOf)(instant, zone);
    }
    catch (error) {
        // zonedPartsOf throws TypeError for a bad instant (a caller's bug, kept
        // loud) and RangeError for a zone Intl does not know.
        if (!(error instanceof RangeError))
            throw error;
        parts = (0, business_day_1.zonedPartsOf)(instant, business_day_1.DEFAULT_VENUE_TIME_ZONE);
    }
    const year = String(parts.year).padStart(4, '0');
    const month = String(parts.month).padStart(2, '0');
    const day = String(parts.day).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
/**
 * Whole days from `today` to `paidUntil`, both `YYYY-MM-DD`: 0 on the last
 * paid day itself, negative once it has passed. Plain date arithmetic at UTC
 * midnight, so no zone's DST change can make a day 23 or 25 hours long.
 *
 * @throws TypeError when either is not a real `YYYY-MM-DD`.
 */
function daysUntil(paidUntil, today) {
    (0, business_day_1.assertBusinessDate)(paidUntil);
    (0, business_day_1.assertBusinessDate)(today);
    return Math.round((Date.parse(`${paidUntil}T00:00:00.000Z`) - Date.parse(`${today}T00:00:00.000Z`)) / MS_PER_DAY);
}
/**
 * True when `paidUntil` is set and strictly before `today`: the venue's
 * subscription has ended, and the hourly sweep suspends it. The paid-until
 * day itself is still paid for. A null `paidUntil` (an unbilled venue, such
 * as Gamers Escape) never lapses.
 */
function paidUntilLapsed(paidUntil, today) {
    if (!paidUntil)
        return false;
    return daysUntil(paidUntil, today) < 0;
}
/**
 * The warning, or null.
 *
 * Null for the demo venue (App Review must never meet a billing banner), for
 * an unbilled venue (`paidUntil` null), and while the last paid day is more
 * than {@link SERVICE_NOTICE_DAYS} away. Otherwise `daysLeft` counts down to
 * 0 on the last paid day, and stays 0 once that day has passed: the sweep
 * runs hourly, so for up to an hour after the venue's midnight a lapsed venue
 * is still ACTIVE, and "ends today" is the truest thing to show until it runs.
 */
function serviceNoticeFor(input) {
    if (input.isDemo === true || !input.paidUntil)
        return null;
    const days = daysUntil(input.paidUntil, input.today);
    if (days > exports.SERVICE_NOTICE_DAYS)
        return null;
    return { paidUntil: input.paidUntil, daysLeft: Math.max(0, days) };
}
//# sourceMappingURL=venue-status.js.map